import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  EditNote as EditNoteIcon,
  AddCircleOutline as AddDamageIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { riderService, vehicleService } from '../services';
import deficitDetailsService from '../services/deficitDetailsService';
import { hasAnyRole } from '../utils/roleUtils';

const canExport = hasAnyRole([
  'Super Admin',
  'EV_Operations_Head',
  'EV_Operations_User',
  'EV_Operations_Manager'
]);
const normalizeDateValue = (value?: string | null) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const formatDateOnly = (value?: string | null) => {
  const date = normalizeDateValue(value);
  if (!date) return value || '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(date);
};

const formatDateTime = (value?: string | null) => {
  const date = normalizeDateValue(value);
  if (!date) return value || '-';

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  }).format(date);
};

const formatTat = (value?: string | null) => {
  if (!value) return '-';

  const matches = [...value.matchAll(/(\d+)\s*([dhms])/gi)];
  if (!matches.length) return value;

  const parts = matches
    .filter(([, amount]) => Number(amount) > 0)
    .map(([, amount, unit]) => `${amount}${unit.toLowerCase()}`);

  return parts.length ? parts.join(' ') : '0s';
};

const getReturnTicketLink = (riderId?: string | null) => {
  let baseUrl = '';
  if (import.meta.env.VITE_NODE_ENV === 'production') {
    baseUrl = (import.meta.env.VITE_API_GATEWAY_URL ?? '').replace(/\/$/, '');
  } else {
    baseUrl = 'http://localhost:5173';
  }

  if (!riderId || !baseUrl) {
    return '';
  }

  return `${baseUrl}/rider-management/${riderId}`;
};

const ReturnTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<any[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [raisedDateFrom, setRaisedDateFrom] = useState('');
  const [raisedDateTo, setRaisedDateTo] = useState('');
  const [appliedRaisedDateFrom, setAppliedRaisedDateFrom] = useState('');
  const [appliedRaisedDateTo, setAppliedRaisedDateTo] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'pending' | 'mis_pending' | 'completed' | 'rejected'>('all');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [ticketToReject, setTicketToReject] = useState<string | null>(null);
  const [rejectingTicketId, setRejectingTicketId] = useState<string | null>(null);
  const [damageCollectionDialogOpen, setDamageCollectionDialogOpen] = useState(false);
  const [damageCollectionSubmitting, setDamageCollectionSubmitting] = useState(false);
  const [damageCollectionError, setDamageCollectionError] = useState<string | null>(null);
  const [damageCollectionForm, setDamageCollectionForm] = useState({
    actualRepairCost: '',
    remarks: '',
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deficitDialogOpen, setDeficitDialogOpen] = useState(false);
  const [deficitSubmitting, setDeficitSubmitting] = useState(false);
  const [deficitRentLoading, setDeficitRentLoading] = useState(false);
  const [deficitDamageCostLoading, setDeficitDamageCostLoading] = useState(false);
  const [deficitDamageCostLoadFailed, setDeficitDamageCostLoadFailed] = useState(false);
  const [deficitError, setDeficitError] = useState<string | null>(null);
  const [deficitForm, setDeficitForm] = useState({
    publicRiderId: '',
    totalRentAmount: '',
    totalEarning: '',
    channelAmount: '',
    damageCost: '',
  });
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    mis_pending: 0,
    completed: 0,
    rejected: 0,
  });

  const fetchStatusCounts = useCallback(async (city: string = 'all') => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return;
      }

      const query = city && city !== 'all' ? `?city=${encodeURIComponent(city)}` : '';
      const response = await fetch(`/api/v1/deployement-return-tickets/return/status-count${query}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch return ticket status counts with status ${response.status}`);
      }

      const payload = await response.json();
      const data = payload?.data ?? {};

      setStatusCounts({
        all: Number(data.all ?? 0),
        pending: Number(data.pending ?? 0),
        mis_pending: Number(data.mis_pending ?? 0),
        completed: Number(data.completed ?? 0),
        rejected: Number(data.rejected ?? 0),
      });
    } catch (err) {
      console.error('Failed to fetch return ticket status counts:', err);
      setStatusCounts({ all: 0, pending: 0, mis_pending: 0, completed: 0, rejected: 0 });
    }
  }, []);

  const fetchReturnTickets = useCallback(async (pageNumber: number = currentPage, pageSize: number = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const statusQuery = selectedStatus !== 'all' ? `&status=${encodeURIComponent(selectedStatus)}` : '';
      const searchQuery = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const raisedDateFromQuery = appliedRaisedDateFrom ? `&dateFrom=${encodeURIComponent(appliedRaisedDateFrom)}` : '';
      const raisedDateToQuery = appliedRaisedDateTo ? `&dateTo=${encodeURIComponent(appliedRaisedDateTo)}` : '';
      const response = await fetch(`/api/v1/deployement-return-tickets/return?page=${pageNumber}&limit=${pageSize}${statusQuery}${searchQuery}${raisedDateFromQuery}${raisedDateToQuery}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(
          errorPayload?.message || `Request failed with status ${response.status}`,
        );
      }

      const payload = await response.json();
      const result = Array.isArray(payload?.data) ? payload.data : [];
      const pagination = payload?.pagination ?? {};
      const nextTotalCount = Number(pagination.totalCount ?? result.length ?? 0);
      const nextTotalPages = Number(pagination.totalPages ?? Math.max(1, Math.ceil(nextTotalCount / pageSize)));

      setTickets(result);
      setFilteredTickets(result);
      setTotalCount(nextTotalCount);
      setTotalPages(nextTotalPages);
    } catch (err: any) {
      console.error('Failed to fetch return tickets:', err);
      setError(err?.message || 'Failed to fetch return tickets');
      setTickets([]);
      setFilteredTickets([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedRaisedDateFrom, appliedRaisedDateTo, currentPage, rowsPerPage, searchTerm, selectedStatus]);

  const fetchCityList = useCallback(async () => {
    try {
      const response = await vehicleService.getCities({ isActive: true });
      const cityList: string[] = Array.isArray(response?.data)
        ? response.data
            .map((city: any) => String(city?.name || city?.city || city?.cityName || '').trim())
            .filter((city): city is string => city.length > 0)
        : [];

      const uniqueCities = Array.from(new Set(cityList)).sort((a, b) => a.localeCompare(b));
      setCities(uniqueCities);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      setCities([]);
    }
  }, []);

  useEffect(() => {
    if (!tickets.length) {
      setFilteredTickets([]);
      return;
    }

    let nextList = tickets;

    if (selectedStatus !== 'all') {
      nextList = nextList.filter((ticket) => {
        return String(ticket?.status ?? '').trim().toLowerCase() === selectedStatus;
      });
    }

    if (selectedCity !== 'all') {
      nextList = nextList.filter((ticket) => {
        return String(ticket?.city ?? '').trim().toLowerCase() === selectedCity.toLowerCase();
      });
    }

    setFilteredTickets(nextList);
  }, [selectedStatus, selectedCity, tickets]);

  useEffect(() => {
    void fetchReturnTickets(currentPage, rowsPerPage);
    void fetchCityList();
    void fetchStatusCounts(selectedCity);
  }, [currentPage, fetchCityList, fetchStatusCounts, fetchReturnTickets, rowsPerPage, selectedCity]);

  const startIndex = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endIndex = totalCount === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalCount);
  const showPagination = filteredTickets.length > 0;

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleRaisedDateFilterApply = () => {
    if (raisedDateFrom && raisedDateTo && raisedDateFrom > raisedDateTo) {
      setError('Ticket Raised On: From date cannot be after the To date.');
      return;
    }

    setError(null);
    setCurrentPage(1);
    setAppliedRaisedDateFrom(raisedDateFrom);
    setAppliedRaisedDateTo(raisedDateTo);
  };

  const handleRaisedDateFilterClear = () => {
    setRaisedDateFrom('');
    setRaisedDateTo('');
    setAppliedRaisedDateFrom('');
    setAppliedRaisedDateTo('');
    setError(null);
    setCurrentPage(1);
  };

  const deficitTotal = ['totalRentAmount', 'channelAmount', 'damageCost'].reduce(
    (total, field) => total + (Number(deficitForm[field as keyof typeof deficitForm]) || 0),
    0,
  );

  const openDeficitDialog = async () => {
    const publicRiderId = String(selectedTicket?.publicRiderId || '').trim();

    setDeficitForm({
      publicRiderId,
      totalRentAmount: '',
      totalEarning: '',
      channelAmount: '',
      damageCost: '',
    });
    setDeficitError(null);
    setDeficitDamageCostLoadFailed(false);
    setDeficitDialogOpen(true);

    if (!publicRiderId) {
      setDeficitError('Public Rider ID is required to fetch the total rent amount.');
      setDeficitDamageCostLoadFailed(true);
      return;
    }

    setDeficitRentLoading(true);
    setDeficitDamageCostLoading(true);
    const [rentResult, damageCostResult] = await Promise.allSettled([
      riderService.getRentValue(publicRiderId),
      deficitDetailsService.getDeficitDetails({ publicRiderId }),
    ]);

    if (rentResult.status === 'fulfilled') {
      setDeficitForm((currentForm) => ({
        ...currentForm,
        totalRentAmount: String(rentResult.value?.data?.TotalRent ?? ''),
        totalEarning: String(rentResult.value?.data?.TotalEarning ?? ''),
      }));
    } else {
      const err = rentResult.reason;
      setDeficitError(err?.response?.data?.message || err?.message || 'Failed to fetch total rent amount');
    }
    setDeficitRentLoading(false);

    if (damageCostResult.status === 'fulfilled' && damageCostResult.value.success) {
      const data = damageCostResult.value.data;
      const details = Array.isArray(data) ? data[0] : data;
      setDeficitForm((currentForm) => ({
        ...currentForm,
        channelAmount: String(details?.channelAmount ?? 0),
        damageCost: String(details?.damageCost ?? 0),
      }));
    } else {
      const err = damageCostResult.status === 'rejected' ? damageCostResult.reason : null;
      setDeficitDamageCostLoadFailed(true);
      setDeficitError((currentError) =>
        [
          currentError,
          err?.response?.data?.message || err?.message || 'Failed to fetch stored damage cost',
        ]
          .filter(Boolean)
          .join('. '),
      );
    }
    setDeficitDamageCostLoading(false);
  };

  const closeDeficitDialog = () => {
    if (!deficitSubmitting) setDeficitDialogOpen(false);
  };

  const openDamageCollectionDialog = () => {
    setDamageCollectionForm({ actualRepairCost: '', remarks: '' });
    setDamageCollectionError(null);
    setDamageCollectionDialogOpen(true);
  };

  const closeDamageCollectionDialog = () => {
    if (!damageCollectionSubmitting) setDamageCollectionDialogOpen(false);
  };

  const handleDamageCollectionSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const publicRiderId = String(selectedTicket?.publicRiderId || '').trim();
    const actualRepairCost = Number(damageCollectionForm.actualRepairCost);
    const remarks = damageCollectionForm.remarks.trim();

    if (String(selectedTicket?.status ?? '').trim().toLowerCase() !== 'mis_pending') {
      setDamageCollectionError('Additional damage can only be recorded for deficit-pending tickets.');
      return;
    }
    if (!publicRiderId) {
      setDamageCollectionError('Public Rider ID is required to record additional damage.');
      return;
    }
    if (!Number.isFinite(actualRepairCost) || actualRepairCost < 0) {
      setDamageCollectionError('Enter a valid, non-negative damage amount.');
      return;
    }
    if (!remarks) {
      setDamageCollectionError('Remark is required.');
      return;
    }

    try {
      setDamageCollectionSubmitting(true);
      setDamageCollectionError(null);
      setSuccessMessage(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(
        `/api/v1/additional-apis-withToken/damage-collection/return-vehicle/${encodeURIComponent(publicRiderId)}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ actualRepairCost, remarks }),
        },
      );
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || `Request failed with status ${response.status}`);
      }

      setDamageCollectionDialogOpen(false);
      setDamageCollectionForm({ actualRepairCost: '', remarks: '' });
      setSuccessMessage('Additional damage details submitted successfully.');
      await Promise.all([
        fetchReturnTickets(currentPage, rowsPerPage),
        fetchStatusCounts(selectedCity),
      ]);
    } catch (err: any) {
      console.error('Failed to submit additional damage details:', err);
      setDamageCollectionError(err?.message || 'Failed to submit additional damage details.');
    } finally {
      setDamageCollectionSubmitting(false);
    }
  };

  const handleDeficitSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (deficitTotal <= 0) {
      setDeficitError('Enter an amount greater than zero in at least one deficit field.');
      return;
    }

    try {
      setDeficitSubmitting(true);
      setDeficitError(null);
      await deficitDetailsService.updateDeficitDetails({
        publicRiderId: deficitForm.publicRiderId.trim(),
        totalRentAmount: Number(deficitForm.totalRentAmount),
        channelAmount: Number(deficitForm.channelAmount),
        damageCost: Number(deficitForm.damageCost),
      });
      await Promise.all([
        fetchReturnTickets(currentPage, rowsPerPage),
        fetchStatusCounts(selectedCity),
      ]);
      setDeficitDialogOpen(false);
      setSelectedTicket(null);
    } catch (err: any) {
      setDeficitError(err?.response?.data?.message || err?.message || 'Failed to update deficit details');
    } finally {
      setDeficitSubmitting(false);
    }
  };

  const handleConfirmRejectTicket = async () => {
    if (!ticketToReject) {
      return;
    }

    const ticketId = ticketToReject;
    try {
      setRejectingTicketId(ticketId);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(`/api/v1/deployement-return-tickets/return/${encodeURIComponent(ticketId)}/status`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'rejected' }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || `Request failed with status ${response.status}`);
      }

      await Promise.all([
        fetchReturnTickets(currentPage, rowsPerPage),
        fetchStatusCounts(selectedCity),
      ]);
      setTicketToReject(null);
      setSelectedTicket(null);
    } catch (err: any) {
      console.error('Failed to reject return ticket:', err);
      setError(err?.message || 'Failed to reject return ticket');
    } finally {
      setRejectingTicketId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Return Tickets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View all return ticket records.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/vehicles')}
          >
            Back to Inventory
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => void fetchReturnTickets()}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 360px', maxWidth: 560 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Search Tickets
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSearch();
            }}
            placeholder="Public Rider ID, phone, or Client Rider ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleSearch}>
                    Search
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 520px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Filter by Ticket Raised On
          </Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={raisedDateFrom}
                onChange={(event) => setRaisedDateFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                value={raisedDateTo}
                inputProps={{ min: raisedDateFrom || undefined }}
                onChange={(event) => setRaisedDateTo(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleRaisedDateFilterApply}>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleRaisedDateFilterClear}>
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <LocationOnIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Cities
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="All Cities"
            onClick={() => setSelectedCity('all')}
            color={selectedCity === 'all' ? 'primary' : 'default'}
            variant={selectedCity === 'all' ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          />
          {cities.map((city) => (
            <Chip
              key={city}
              label={city}
              onClick={() => setSelectedCity(city)}
              color={selectedCity === city ? 'primary' : 'default'}
              variant={selectedCity === city ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'All', status: 'all' as const, value: statusCounts.all, color: '#2563eb' },
          { label: 'Pending', status: 'pending' as const, value: statusCounts.pending, color: '#f59e0b' },
          { label: 'Deficit Pending', status: 'mis_pending' as const, value: statusCounts.mis_pending, color: '#7c3aed' },
          { label: 'Completed', status: 'completed' as const, value: statusCounts.completed, color: '#16a34a' },
          { label: 'Rejected', status: 'rejected' as const, value: statusCounts.rejected, color: '#dc2626' },
        ].map((item) => {
          const isActive =
            item.status === selectedStatus;

          return (
            <Grid item xs={12} sm={6} md={2.4} key={item.label}>
              <Card
                variant="outlined"
                onClick={() => {
                  setSelectedStatus(item.status);
                }}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: item.color }}>
                    {item.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ border: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Public ID(EV91 ID)</TableCell>
                    <TableCell>Rider Name</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source Name</TableCell>
                    <TableCell>Ticket Raised On</TableCell>
                    <TableCell>TAT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        {selectedStatus === 'all' && selectedCity === 'all'
                          ? 'No return tickets found.'
                          : selectedStatus !== 'all' && selectedCity === 'all'
                            ? `No ${selectedStatus} return tickets found.`
                            : selectedStatus === 'all' && selectedCity !== 'all'
                              ? `No return tickets found for ${selectedCity}.`
                              : `No ${selectedStatus} return tickets found for ${selectedCity}.`}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((ticket) => {
                      return (
                        <TableRow
                          key={ticket.id}
                          hover
                          tabIndex={0}
                          role="button"
                          aria-label={`View return ticket for ${ticket.publicRiderId || ticket.phone || 'rider'}`}
                          onClick={() => setSelectedTicket(ticket)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setSelectedTicket(ticket);
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: '-2px',
                            },
                          }}
                        >
                          <TableCell>{ticket.publicRiderId || '-'}</TableCell>
                          <TableCell>{ticket.rider?.name || '-'}</TableCell>
                          <TableCell>{ticket.clientName || '-'}</TableCell>
                          <TableCell>{ticket.phone || '-'}</TableCell>
                          <TableCell>{ticket.city || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={ticket.status  === 'mis_pending' ? 'Deficit Pending' : ticket.status || 'pending'}
                              size="small"
                              color={
                                ticket.status === 'mis_pending'
                                ? 'info'
                                : ticket.status === 'pending'
                                  ? 'warning'
                                  : ticket.status === 'rejected'
                                    ? 'error'
                                    : 'success'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{ticket.referredById || '-'}</TableCell>
                          <TableCell>{formatDateTime(ticket.createdAt)}</TableCell>
                          <TableCell>{formatTat(ticket.totalTimeDifference)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={selectedTicket !== null}
        onClose={() => setSelectedTicket(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.24)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Typography variant="overline" color="primary.main" fontWeight={800} letterSpacing={1}>
            Return ticket details
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {selectedTicket?.publicRiderId || selectedTicket?.riderId || 'Ticket'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ backgroundColor: '#f8fafc' }}>
          {selectedTicket && (
            <>
              <Grid container spacing={2}>
                {[
                  ['Public Rider ID', selectedTicket.publicRiderId],
                  ['Rider Name', selectedTicket.rider?.name],
                  ['Internal Rider ID', selectedTicket.riderId],
                  ['Client', selectedTicket.clientName],
                  ['Client Rider ID', selectedTicket.clientRiderId],
                  ['Phone', selectedTicket.phone],
                  ['City', selectedTicket.city],
                  ['Onboarded Date', formatDateOnly(selectedTicket.onboardedDate)],
                  ['Vehicle Number', selectedTicket.vehicleNumber],
                  ['Assigned Vehicle ID', selectedTicket.assignedVehicleId],
                  ['Assignment Date', formatDateOnly(selectedTicket.assignmentDate)],
                  ['Source Name', selectedTicket.referredById],
                  ['Ticket Raised By', selectedTicket.raisedBy],
                  ['Ticket Raised On', formatDateTime(selectedTicket.createdAt)],
                  ['Ticket Close Date', formatDateTime(selectedTicket.ticketCloseDate)],
                  ['TAT', formatTat(selectedTicket.totalTimeDifference)],
                  ['Rejected By', selectedTicket.rejectedBy],
                  ['Paying Security Deposit', selectedTicket.payingSD == null ? '-' : `₹${Number(selectedTicket.payingSD).toLocaleString('en-IN')}`],
                  ['Reason', selectedTicket.reason],
                  ['Status', selectedTicket.status || 'pending'],
                  ['Ticket ID', selectedTicket.id],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Box sx={{ height: '100%', p: 1.5, backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                        {label}
                      </Typography>
                      {label === 'Status' ? (
                        <Chip
                          label={String(value)}
                          size="small"
                          color={selectedTicket.status === 'rejected' ? 'error' : selectedTicket.status === 'pending' ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                          {value || '-'}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {String(selectedTicket.status ?? '').trim().toLowerCase() === 'mis_pending' && (
                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{ mt: 2, borderWidth: 2, alignItems: 'center' }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="contained"
                      startIcon={<AddDamageIcon />}
                      onClick={openDamageCollectionDialog}
                      disabled={!selectedTicket.publicRiderId}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Add additional damage
                    </Button>
                  }
                >
                  <AlertTitle sx={{ fontWeight: 800 }}>Update deficit details</AlertTitle>
                  If rider has any damage amount while returning the vehicle, please update it here.
                  {!selectedTicket.publicRiderId && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      A Public Rider ID is required to add damage details.
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => setSelectedTicket(null)}>
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedTicket && String(selectedTicket.status).toLowerCase() === 'mis_pending' && (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditNoteIcon />}
                  onClick={openDeficitDialog}
                  disabled={!canExport}
                >
                  Update deficit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setTicketToReject(selectedTicket.id);
                    setSelectedTicket(null);
                  }}
                  disabled={rejectingTicketId !== null}
                >
                  Reject ticket
                </Button>
              </>
            )}
            {selectedTicket && String(selectedTicket.status).toLowerCase() === 'pending' && (
              getReturnTicketLink(selectedTicket.riderId || selectedTicket.rider_id || selectedTicket.id) && (
                <Button
                  component="a"
                  href={getReturnTicketLink(selectedTicket.riderId || selectedTicket.rider_id || selectedTicket.id)}
                  target="_blank"
                  rel="noreferrer"
                  variant="contained"
                >
                  Open return page
                </Button>
              )
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ticketToReject !== null}
        onClose={() => {
          if (!rejectingTicketId) {
            setTicketToReject(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.24)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700 }}>
          <WarningAmberIcon color="error" />
          Reject return ticket?
        </DialogTitle>
        <DialogContent sx={{ color: 'text.secondary', pt: 0.5 }}>
          This action will mark the deficit-pending ticket as rejected. Are you sure you want to continue?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setTicketToReject(null)}
            disabled={rejectingTicketId !== null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleConfirmRejectTicket()}
            disabled={rejectingTicketId !== null}
          >
            {rejectingTicketId !== null ? 'Rejecting...' : 'Yes, Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={damageCollectionDialogOpen}
        onClose={closeDamageCollectionDialog}
        fullWidth
        maxWidth="sm"
      >
        <Box component="form" onSubmit={handleDamageCollectionSubmit}>
          <DialogTitle>Add additional damage</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter any damage amount identified while returning the vehicle for{' '}
              <strong>{selectedTicket?.publicRiderId || '-'}</strong>.
            </Typography>
            {damageCollectionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {damageCollectionError}
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField
                autoFocus
                fullWidth
                required
                type="number"
                label="Damage amount"
                value={damageCollectionForm.actualRepairCost}
                onChange={(event) =>
                  setDamageCollectionForm((form) => ({
                    ...form,
                    actualRepairCost: event.target.value,
                  }))
                }
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="Remark"
                value={damageCollectionForm.remarks}
                onChange={(event) =>
                  setDamageCollectionForm((form) => ({
                    ...form,
                    remarks: event.target.value,
                  }))
                }
                inputProps={{ maxLength: 500 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDamageCollectionDialog} disabled={damageCollectionSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                damageCollectionSubmitting ||
                damageCollectionForm.actualRepairCost === '' ||
                !damageCollectionForm.remarks.trim()
              }
              startIcon={
                damageCollectionSubmitting
                  ? <CircularProgress size={18} />
                  : <AddDamageIcon />
              }
            >
              {damageCollectionSubmitting ? 'Submitting...' : 'Submit damage'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deficitDialogOpen} onClose={closeDeficitDialog} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleDeficitSubmit}>
          <DialogTitle sx={{ pb: 1 }}>Update deficit details</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the current deficit values for this rider. Existing values will be replaced.
            </Typography>
            {deficitError && <Alert severity="error" sx={{ mb: 2 }}>{deficitError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Public Rider ID"
                  value={deficitForm.publicRiderId}
                  onChange={(event) => setDeficitForm({ ...deficitForm, publicRiderId: event.target.value })}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Total Rider Earning"
                  value={deficitForm.totalEarning}
                  disabled
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography>,
                  }}
                />
              </Grid>
              {[
                ['totalRentAmount', 'Total rent amount'],
                ['channelAmount', 'Channel amount'],
                ['damageCost', 'Damage cost'],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={4} key={field}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label={label}
                    value={deficitForm[field as keyof typeof deficitForm]}
                    onChange={field === 'damageCost'
                      ? undefined
                      : (event) => setDeficitForm({ ...deficitForm, [field]: event.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                    disabled={
                      (field === 'totalRentAmount' && deficitRentLoading) ||
                      (field === 'damageCost' && deficitDamageCostLoading)
                    }
                    InputProps={{
                      readOnly: field === 'damageCost',
                      startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography>,
                      endAdornment: (field === 'totalRentAmount' && deficitRentLoading) ||
                        (field === 'damageCost' && deficitDamageCostLoading)
                        ? <CircularProgress size={18} />
                        : undefined,
                    }}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'action.hover' }}>
                  <Typography variant="subtitle2" color="text.secondary">Total amount</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(deficitTotal)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDeficitDialog} disabled={deficitSubmitting}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={deficitSubmitting || deficitDamageCostLoading || deficitDamageCostLoadFailed}
              startIcon={deficitSubmitting ? <CircularProgress size={18} /> : <EditNoteIcon />}
            >
              {deficitSubmitting ? 'Saving...' : 'Save deficit details'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mt: 2,
            p: 2,
            border: '2px solid #dbeafe',
            borderRadius: 2,
            backgroundColor: '#f8fbff',
            width: '100%',
            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.08)',
            flexWrap: 'wrap',
            position: 'sticky',
            bottom: 0,
            zIndex: 50,
            left: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Showing {startIndex}-{endIndex} of {totalCount || filteredTickets.length}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="medium"
              disabled={currentPage === 1}
              onClick={handlePreviousPage}
              sx={{ minWidth: 100, fontWeight: 700 }}
            >
              Previous
            </Button>

            <Typography
              variant="body2"
              sx={{
                minWidth: 96,
                textAlign: 'center',
                color: 'text.secondary',
                fontWeight: 700,
              }}
            >
              Page {currentPage} of {totalPages}
            </Typography>

            <Button
              variant="contained"
              size="medium"
              disabled={currentPage >= totalPages}
              onClick={handleNextPage}
              sx={{ minWidth: 100, fontWeight: 700 }}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

    </Box>
  );
};

export default ReturnTicketsPage;
